/**
 * UX Principles ページ
 *
 * @graph-stack creative
 * @graph-domain Marketing
 * @graph-business P1〜P4 のプリンシパルカードと UX Goals の表示・編集 (canEdit のみ)。
 * @graph-connects api [calls] principles.list / principles.update
 */
import { useToast } from '@cortex/web-design-system/components/ui/Toast';
import type { Principle } from '@cortex/domain-creative/web';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type JSX } from 'react';
import { orpc } from '../api/orpc.js';
import { useAuth } from '../components/auth-context.js';
import { principleColorVar } from '../lib/labels.js';
import {
  EmptyState,
  Field,
  GhostButton,
  GlassCard,
  INPUT_CLASS,
  Modal,
  PrimaryButton,
  SectionHeading,
} from '../components/ui.js';

/** 編集ドラフト。 */
interface PrincipleDraft {
  code: Principle['code'];
  name: string;
  nameJa: string;
  uxGoalsText: string;
}

/**
 * UX Principles ページ。
 * @returns ページ要素
 * @graph-connects api [calls] principles.list / principles.update
 */
export function PrinciplesPage(): JSX.Element {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const principlesQuery = useQuery(orpc.principles.list.queryOptions({ input: {} }));
  const principles = principlesQuery.data?.items ?? [];

  const [draft, setDraft] = useState<PrincipleDraft | null>(null);

  const updateMutation = useMutation(
    orpc.principles.update.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: orpc.principles.key() });
        setDraft(null);
        showToast('success', 'Principle を更新しました');
      },
      onError: (error) => {
        showToast('error', error.message);
      },
    }),
  );

  return (
    <div>
      <SectionHeading title="UX Principles" />
      {principles.length === 0 ? (
        <EmptyState message="Principle が未登録です (seed 未実行)" />
      ) : (
        <div className="grid grid-cols-2 gap-3">
          {principles.map((principle) => (
            <GlassCard key={principle.code} className="transition hover:-translate-y-px">
              <div className="flex items-start justify-between">
                <div
                  className="mb-1.5 font-mono text-micro font-bold tracking-label"
                  style={{ color: `var(${principleColorVar(principle.colorKey)})` }}
                >
                  {principle.code} / {principle.coreValueId}
                </div>
                {auth.canEdit && (
                  <GhostButton
                    onClick={() => {
                      setDraft({
                        code: principle.code,
                        name: principle.name,
                        nameJa: principle.nameJa,
                        uxGoalsText: principle.uxGoals.join('\n'),
                      });
                    }}
                  >
                    編集
                  </GhostButton>
                )}
              </div>
              <div className="text-title font-bold text-foreground">{principle.name}</div>
              <div className="mb-3 text-xs text-subtle">{principle.nameJa}</div>
              <ul className="list-disc space-y-1 pl-4 text-xs text-subtle">
                {principle.uxGoals.map((goal) => (
                  <li key={goal}>{goal}</li>
                ))}
              </ul>
            </GlassCard>
          ))}
        </div>
      )}

      <Modal
        open={draft !== null}
        title={`${draft?.code ?? ''} を編集`}
        onClose={() => {
          setDraft(null);
        }}
      >
        {draft && (
          <>
            <Field label="名前 (英語)">
              <input
                className={INPUT_CLASS}
                value={draft.name}
                onChange={(event) => {
                  setDraft({ ...draft, name: event.target.value });
                }}
              />
            </Field>
            <Field label="名前 (日本語)">
              <input
                className={INPUT_CLASS}
                value={draft.nameJa}
                onChange={(event) => {
                  setDraft({ ...draft, nameJa: event.target.value });
                }}
              />
            </Field>
            <Field label="UX Goals (1 行 1 項目)">
              <textarea
                className={`${INPUT_CLASS} h-36 resize-y font-mono text-xs`}
                value={draft.uxGoalsText}
                onChange={(event) => {
                  setDraft({ ...draft, uxGoalsText: event.target.value });
                }}
              />
            </Field>
            <div className="mt-4 flex justify-end gap-2">
              <GhostButton
                onClick={() => {
                  setDraft(null);
                }}
              >
                キャンセル
              </GhostButton>
              <PrimaryButton
                disabled={updateMutation.isPending}
                onClick={() => {
                  updateMutation.mutate({
                    code: draft.code,
                    patch: {
                      name: draft.name,
                      nameJa: draft.nameJa,
                      uxGoals: draft.uxGoalsText
                        .split('\n')
                        .map((line) => line.trim())
                        .filter((line) => line !== ''),
                    },
                  });
                }}
              >
                保存
              </PrimaryButton>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}
